import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import useApi from '../../hooks/useApi';
import NodeContentTopSection from '../NodeContentTopSection';
import NodeContentHeaderSection from '../NodeContentHeaderSection';
import NodeContentFooterSection from '../NodeContentFooterSection';
import NodeContentSection from '../NodeContentSection';
import TaskContentViewer from './nodes/TaskContentViewer';
import ChunkContentViewer from './nodes/ChunkContentViewer';
import WrittenQuizContentViewer from './nodes/WrittenQuizContentViewer';
import QuizContentViewer from './nodes/QuizContentViewer';
import ChecklistContentViewer from './nodes/ChecklistContentViewer';
import EvaluationContentViewer from './nodes/EvaluationContentViewer';
import PublicBlogContentViewer from './nodes/PublicBlogContentViewer';
import PrivateBlogContentViewer from './nodes/PrivateBlogContentViewer';
import GlossaryContentViewer from './nodes/GlossaryContentViewer';
import CorrelationContentViewer from './nodes/CorrelationContentViewer';
import { debounce, getAutoSaveInterval } from '../../utils';

import { PanelWrapper } from './styles';

const NodeContentViewer = ({
  nodeId,
  type: baseType,
  userToken,
  fileService,
  options,
  panel,
  flash,
  readOnly = false,
}) => {
  const { session_id, session_uuid, navigation, integrations, setCheckmark, viewer } = options;
  const { getData, loading } = useApi(userToken);
  const [title, setTitle] = useState('');
  const [user, setUser] = useState();
  const { postData } = useApi(userToken);
  const [updatedAt, setUpdatedAt] = useState();
  const [isUpdating, setIsUpdating] = useState(false);
  const [description, setDescription] = useState('');
  const [type, setType] = useState('');
  const [report, setReport] = useState();
  const [reports, setReports] = useState([]);
  const [data, setData] = useState();
  const [content, setContent] = useState();
  const [isTeacher, setIsTeacher] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState({});
  const [contextId, setContextId] = useState('');
  const [participations, setParticipations] = useState([]);

  const isArticle = ['chunk', 'video', 'link'].includes(baseType);
  const nodeType = isArticle ? 'chunks' : 'behaviors';

  useEffect(() => {
    const getNodeData = async () => {
      const response = await getData(`/api/v3/viewer/${nodeType}/${nodeId}/session/${session_uuid}`);
      if (response.success) {
        const body = response.body;
        setIsUpdating(true);
        setIsTeacher(body.role === 'teacher');
        setTitle(body.name || '');
        setType(body.type || '');
        setUser(body.user);
        setUpdatedAt(body.updated_at);
        setDescription((isArticle ? body.data.content : body.data.body) || '');
        setContextId(body.data.uuid);
        setReport(body.report ? { ...body.report, editorId: 'contentEditor' } : null);
        setReports(body.reports);
        setData(body.data);
        setContent(body);
        setParticipations(body.participations);
        setIsUpdating(false);
      }
    };
    getNodeData();
  }, []);

  if (!window.autoSaveTimer) {
    window.autoSaveTimer = {};
  }

  const debouncedSave = useCallback(
    debounce(async (newReport, timestamp, content) => {
      await save(newReport, timestamp, content);
      window.autoSaveTimer[newReport.editorId] = setTimeout(() => {
        setAutoSaveStatus({ ...autoSaveStatus, [newReport.editorId]: '' });
      }, 5000);
    }, getAutoSaveInterval()),
    [],
  );

  useEffect(() => {
    if (updatedAt && !isTeacher && !isUpdating && report.save) {
      setAutoSaveStatus({ ...autoSaveStatus, [report.editorId]: i18n.__('behavior_saving') });
      clearTimeout(window.autoSaveTimer[report.editorId]);
      debouncedSave(report, updatedAt, content);
    }
  }, [report]);

  const toggleComments = () => {
    const detail = { entityId: nodeId, entityType: 'node' };
    document.dispatchEvent(new CustomEvent('toggleComments', { detail }));
  };

  const markAcceptedNode = (uuid, state) => {
    const types = ['quiz', 'chunk', 'legend', 'correlation'];
    if (state === 'accept' && types.includes(baseType)) {
      viewer.addCheckmarkById(uuid);
    } else {
      viewer.removeCheckmarkById(uuid, state);
    }
  };

  const navigateNext = (doneProgress) => {
    const navigationNext = document.querySelector('.navigation-arrow--right');
    if (doneProgress === 1 || !navigationNext) {
      panel.hide();
    } else {
      navigationNext.click();
    }
  };

  const saveReport = async (updatedReport) => {
    setReport({ ...updatedReport, save: true });
  };

  const save = async (newReport, timestamp, content) => {
    if (readOnly) {
      return;
    }

    const { uuid, body, state, draft: isDraft, answers, list, editorId, post_uuid } = newReport;
    const payload = { reportId: uuid, body, state, isDraft, answers, list, updated_at: timestamp, post_uuid };
    const response = await postData(`/api/v3/viewer/${nodeType}/${nodeId}/session/${session_uuid}/report`, payload);
    if (response.success) {
      setIsUpdating(true);
      const isDone = state === 'done';
      const isComplete = state === 'accept';
      setAutoSaveStatus({ ...autoSaveStatus, [editorId]: i18n.__('behavior_saved') });
      const { updated_at, done_progress, data } = response.body;
      setUpdatedAt(updated_at);
      setCheckmark(isDone || isComplete);
      markAcceptedNode(nodeId, state);
      document.dispatchEvent(new CustomEvent('progressUpdated', { detail: response.body }));
      if (isDone && !isDraft) {
        navigateNext(done_progress);
      }
      if (data) {
        const { body, ...newData } = data;
        const updatedReport = { ...newReport, ...newData, save: false };
        setContent({ ...content, report: updatedReport });
        setReport(updatedReport);
      }
      setIsUpdating(false);
    } else {
      setAutoSaveStatus({ ...autoSaveStatus, [editorId]: i18n.__('behavior_save_failed') });
      console.error(response);
      if (response.message === 'auth_failed') {
        flash(i18n.__('app_error_unauthorized'), { ttl: 5000 });
      } else if (response.message === 'edit_conflict') {
        flash(i18n.__(`error_edit_conflict_${baseType}`), { ttl: 60000 });
      } else if (response.message === 'payload_too_large') {
        flash(i18n.__('error_payload_too_large'), { ttl: 20000 });
      } else {
        flash(i18n.__('error_behavior_save_failed'));
      }
    }
  };

  if (!data) {
    return null;
  }

  const topSection = (
    <NodeContentTopSection
      nodeType={type}
      showFullScreenTool={true}
      showVoiceOverTool={true}
      showTeacherPanelLink={isTeacher}
      nodeId={nodeId}
      sessionId={session_id}
      panel={panel}
      viewer={viewer}
      userToken={userToken}
      nodeData={content}
      title={title}
    />
  );

  const headerSection = <NodeContentHeaderSection title={title} toggleComments={toggleComments} />;

  const contentSection = (
    <NodeContentSection description={description} type={baseType === 'chunk' ? baseType : type} contextId={contextId} />
  );

  const isEditable = !isTeacher && !report?.locked && ['blank', 'active'].includes(report?.state);

  const viewSection = () => {
    const getViewComponent = () => {
      switch (type) {
        case 'chunk':
        case 'video':
        case 'link':
          return ChunkContentViewer;
        case 'task':
        case 'group':
          return TaskContentViewer;
        case 'evaluation':
          return EvaluationContentViewer;
        case 'written_quiz':
          return WrittenQuizContentViewer;
        case 'quiz':
          return QuizContentViewer;
        case 'checklist':
          return ChecklistContentViewer;
        case 'private_blog':
          return PrivateBlogContentViewer;
        case 'public_blog':
          return PublicBlogContentViewer;
        case 'legend':
          return GlossaryContentViewer;
        case 'correlation':
          return CorrelationContentViewer;
      }
    };

    const ViewComponent = getViewComponent();
    return (
      <ViewComponent
        type={type}
        report={report}
        reports={reports}
        isEditable={isEditable}
        saveReport={saveReport}
        data={data}
        fileService={fileService}
        integrations={integrations}
        autoSaveStatus={autoSaveStatus}
        isTeacher={isTeacher}
        readOnly={readOnly}
        participations={participations}
        user={user}
        flash={flash}
        sessionId={session_uuid}
        nodeId={nodeId}
      />
    );
  };

  const useCommonSubmit =
    ['chunk', 'video', 'link', 'written_quiz', 'quiz', 'private_blog', 'legend', 'correlation'].includes(type) &&
    !isTeacher;

  const footerSection = <NodeContentFooterSection navigation={navigation} useCommonSubmit={useCommonSubmit} />;

  return (
    !loading && (
      <PanelWrapper className={'ir-content'}>
        {topSection}
        {headerSection}
        {contentSection}
        {viewSection()}
        {footerSection}
      </PanelWrapper>
    )
  );
};

export default NodeContentViewer;

NodeContentViewer.propTypes = {
  nodeId: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  userToken: PropTypes.string.isRequired,
  fileService: PropTypes.object.isRequired,
  options: PropTypes.object.isRequired,
  panel: PropTypes.object.isRequired,
  flash: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};
